import axios from 'axios';
import { BACKEND_URL, STRAPI_TOKEN } from './config';
import { PlanActionsContext, ReportApiResponse, ReportFilters } from '../types/types';

export const baseUrl = `${BACKEND_URL}/api`;

export const getAuthConfig = () => {
  return {
    headers: {
      Authorization: `Bearer ${STRAPI_TOKEN}`,
    },
    withCredentials: true,
  };
};

export const fetchStrapiData = (modelName: string, params = {}) => {
  return axios.get(`${baseUrl}/${modelName}?populate=*`, {
    ...getAuthConfig(),
    params,
  });
};

export const addStrapiData = (modelName: string, data: any) => {
  const formattedData = data.data ? data : { data };
  return axios.post(`${baseUrl}/${modelName}`, formattedData, getAuthConfig()).catch((error) => {
    console.error('Ошибка API:', error.response?.data || error.message);
    throw error;
  });
};

export const deleteStrapiData = (modelName: string, id: number) => {
  // console.log('deleteData', { modelName, id, url: `${baseUrl}/${modelName}/${id}` });

  return axios
    .delete(`${baseUrl}/${modelName}/${id}`, getAuthConfig())
    .then((response) => {
      // console.log('Запись удалена', response.data);
      return response;
    })
    .catch((error) => {
      console.error('Ошибка:', error.response?.data || error.message);
      throw error;
    });
};

export const uploadFileToStrapi = async (file: File) => {
  const formData = new FormData();
  formData.append('files', file);
  try {
    // console.log('файл:', file.name, 'размер:', file.size);
    const response = await axios.post(`${baseUrl}/upload`, formData, {
      headers: {
        Authorization: `Bearer ${STRAPI_TOKEN}`,
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error('Ошибка:', error.response?.data || error);
    throw error;
  }
};

export const createGopUploadWithFile = async (file: File, refId: number, title: string) => {
  try {
    const uploadedFiles = await uploadFileToStrapi(file);

    if (!uploadedFiles || uploadedFiles.length === 0) {
      throw new Error('Файл не был загружен');
    }

    const uploadedFile = uploadedFiles[0];

    const uploadData = {
      data: {
        Title: title,
        CommentId: refId,
        Docs: uploadedFile.id,
      },
    };

    const recordResponse = await addStrapiData('gop-uploads', uploadData);
    const createdRecord = extractStrapiData(recordResponse);

    return {
      record: createdRecord,
      file: uploadedFile,
    };
  } catch (error) {
    console.error('Ошибка при создании записи с файлом:', error);
    throw error;
  }
};

export const createGopCommentOnly = async (refId: number, title: string) => {
  try {
    const uploadData = {
      data: {
        Title: title,
        CommentId: refId,
      },
    };

    const recordResponse = await addStrapiData('gop-uploads', uploadData);
    const createdRecord = extractStrapiData(recordResponse);

    return {
      record: createdRecord,
      file: null,
    };
  } catch (error) {
    console.error('Ошибка при создании записи без файла:', error);
    throw error;
  }
};

export const createGopUploadWithFileSimple = async (file: File, refId: number, title: string) => {
  try {
    const formData = new FormData();

    formData.append('files.Docs', file);

    formData.append(
      'data',
      JSON.stringify({
        Title: title,
        CommentId: refId,
      })
    );

    const response = await axios.post(`${baseUrl}/gop-uploads`, formData, {
      headers: {
        Authorization: `Bearer ${STRAPI_TOKEN}`,
      },
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error('Ошибка при создании записи:', error);
    throw error;
  }
};

export const deleteFileFromStrapi = async (fileId: number) => {
  return axios.delete(`${baseUrl}/upload/files/${fileId}`, getAuthConfig());
};

export const extractStrapiData = (response: any) => {
  if (response && response.data && response.data.data) {
    return response.data.data;
  }
  if (response && response.data) {
    return response.data;
  }
  return [];
};

export const updateStrapiData = (modelName: string, documentId: string, data: any) => {
  const formattedData = data.data ? data : { data };
  return axios.put(`${baseUrl}/${modelName}/${documentId}`, formattedData, getAuthConfig()).catch((error) => {
    console.error('Ошибка API при обновлении:', error.response?.data || error.message);
    throw error;
  });
};

// export const fetchReportData = async (filters: ReportFilters): Promise<{answers: any[], statuses: any[]}> => {
//   try {
//     const [answers, statuses] = await Promise.all([
//       fetchReportAnswers(filters),
//       fetchReportStatuses(filters)
//     ]);
//     return { answers, statuses };
//   } catch (error: any) {
//     console.error('Ошибка при загрузке данных:', error);
//     throw error;
//   }
// };

export const fetchReportActions = async (filters: ReportFilters): Promise<any[]> => {
  try {
    const response = await axios.get<ReportApiResponse<any>>(`${baseUrl}/gop-data-report-period/actions`, {
      ...getAuthConfig(),
      params: filters,
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'Ошибка при загрузке действий');
    }
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Ошибка при загрузке действий');
  }
};

export const fetchDataByContext = async (modelName: string, context: PlanActionsContext): Promise<any> => {
  try {
    const filters: Record<string, any> = {};
    if (context.currentRequirement?.id) {
      filters['demandID'] = context.currentRequirement.id;
    }
    if (context.currentBlock?.id) {
      filters['blockID'] = context.currentBlock.id;
    }
    if (context.selectedFactory?.id) {
      filters['factory'] = context.selectedFactory.id;
    }
    if (context.selectedGop?.id) {
      filters['gop'] = context.selectedGop.id;
    }
    if (context.currentDirection?.id) {
      filters['directionID'] = context.currentDirection.id;
    }
    if (context.currentQuarter && modelName !== 'gop-actions') {
      filters['quarter'] = context.currentQuarter;
    }
    if (context.currentYear && modelName !== 'gop-actions') {
      filters['year'] = context.currentYear;
    }
    if (context.selectedGroup?.id) {
      filters['group'] = context.selectedGroup.id;
    }
    const params: Record<string, any> = { populate: '*' };
    Object.entries(filters).forEach(([key, value]) => {
      params[`filters[${key}]`] = value;
    });
    const response = await axios.get(`${baseUrl}/${modelName}`, {
      ...getAuthConfig(),
      params,
    });
    if (response.data?.data) {
      return response.data.data;
    } else {
      return [];
    }
  } catch (error: any) {
    console.error(`Ошибка при поиске данных из модели ${modelName}:`, error);
    if (error.response?.data?.error) {
      console.error('Детали ошибки:', error.response.data);
      throw new Error(error.response.data.error);
    }
    throw new Error(`Ошибка при поиске данных в модели ${modelName}`);
  }
};

//! для получения карточки с ID relations
export const fetchActionById = async (actionId: number): Promise<any> => {
  try {
    const response = await axios.get(
      `${baseUrl}/gop-actions?filters[id][$eq]=${actionId}&populate[factory][fields]=id&populate[gop][fields]=id&populate[group][fields]=id&fields[0]=quarter&fields[1]=year&fields[2]=directionID`,
      getAuthConfig()
    );

    if (response.data?.data && response.data.data.length > 0) {
      return response.data.data[0];
    }
    throw new Error('Карточка не найдена');
  } catch (error: any) {
    console.error(`Ошибка получения карточки ID ${actionId}:`, error);
    throw error;
  }
};

export const fetchReportDataComplete = async (filters: ReportFilters): Promise<{ answers: any[]; statuses: any[] }> => {
  try {
    const params = new URLSearchParams();
    if (filters.factories && filters.factories.length > 0) {
      filters.factories.forEach((factory) => params.append('factories', factory));
    }
    if (filters.directions && filters.directions.length > 0) {
      filters.directions.forEach((direction) => params.append('directions', direction));
    }
    if (filters.quarters && filters.quarters.length > 0) {
      filters.quarters.forEach((quarter) => params.append('quarters', quarter));
    }
    if (filters.years && filters.years.length > 0) {
      filters.years.forEach((year) => params.append('years', year));
    }
    if (filters.gops && filters.gops.length > 0) {
      filters.gops.forEach((gop) => params.append('gops', gop));
    }
    if (filters.isFocal !== undefined) {
      params.append('isFocal', filters.isFocal.toString());
    }
    const url = `${baseUrl}/gop-data-report-period/complete?${params.toString()}`;
    const response = await axios.get<ReportApiResponse<any>>(url, getAuthConfig());

    if (response.data.success) {
      return {
        answers: response.data.data.answers,
        statuses: response.data.data.statuses,
      };
    } else {
      throw new Error(response.data.error || 'Ошибка при загрузке данных отчета');
    }
  } catch (error: any) {
    console.error('Ошибка при запросе', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Ошибка при загрузке данных отчета');
  }
};

export const fetchKPIDataComplete = async (filters: any): Promise<{ answers: any[]; statuses: any[] }> => {
  try {
    const params = new URLSearchParams();
    if (filters.factories && filters.factories.length > 0) {
      filters.factories.forEach((factory: string) => params.append('factories', factory));
    }
    if (filters.directions && filters.directions.length > 0) {
      filters.directions.forEach((direction: string) => params.append('directions', direction));
    }
    if (filters.months && filters.months.length > 0) {
      filters.months.forEach((month: string) => params.append('months', month));
    }
    if (filters.years && filters.years.length > 0) {
      filters.years.forEach((year: string) => params.append('years', year));
    }
    if (filters.gops && filters.gops.length > 0) {
      filters.gops.forEach((gop: string) => params.append('gops', gop));
    }
    if (filters.blocks && filters.blocks.length > 0) {
      filters.blocks.forEach((block: string) => params.append('blocks', block));
    }
    if (filters.departments && filters.departments.length > 0) {
      filters.departments.forEach((department: string) => params.append('departments', department));
    }
    if (filters.kpiNames && filters.kpiNames.length > 0) {
      filters.kpiNames.forEach((kpiName: string) => params.append('kpiNames', kpiName));
    }
    if (filters.isFocal !== undefined) {
      params.append('isFocal', filters.isFocal.toString());
    }
    const url = `${baseUrl}/gop-data-report-kpi/complete?${params.toString()}`;
    const response = await axios.get<ReportApiResponse<any>>(url, getAuthConfig());
    if (response.data.success) {
      return {
        answers: response.data.data.answers,
        statuses: response.data.data.statuses,
      };
    } else {
      throw new Error(response.data.error || 'Ошибка при загрузке данных KPI');
    }
  } catch (error: any) {
    console.error('Ошибка при запросе KPI', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Ошибка при загрузке данных KPI');
  }
};

export const fetchKPIChartData = async (filters: any): Promise<{ kpiPlan: any[]; kpiFact: any[] }> => {
  try {
    const params = new URLSearchParams();
    if (filters.factories && filters.factories.length > 0) {
      filters.factories.forEach((factory: string) => params.append('factories', factory));
    }
    if (filters.months && filters.months.length > 0) {
      filters.months.forEach((month: string) => params.append('months', month));
    }
    if (filters.years && filters.years.length > 0) {
      filters.years.forEach((year: string) => params.append('years', year));
    }
    if (filters.kpiNames && filters.kpiNames.length > 0) {
      filters.kpiNames.forEach((kpiName: string) => params.append('kpiNames', kpiName));
    }
    const url = `${baseUrl}/gop-data-report-kpi/chart-data?${params.toString()}`;
    const response = await axios.get<ReportApiResponse<any>>(url, getAuthConfig());
    if (response.data.success) {
      return {
        kpiPlan: response.data.data.kpiPlan,
        kpiFact: response.data.data.kpiFact,
      };
    } else {
      throw new Error(response.data.error || 'Ошибка при загрузке данных графика KPI');
    }
  } catch (error: any) {
    console.error('Ошибка при запросе данных графика KPI', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Ошибка при загрузке данных графика KPI');
  }
};
